import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiJson } from '../api/client.js';
import { FormInput } from '../components/FormControls.jsx';

export function SuperAdminClientsPage() {
  const [searchParams] = useSearchParams();
  const [page, setPage] = useState({ data: [], total: 0, last_page: 1, current_page: 1 });
  const [search, setSearch] = useState(() => searchParams.get('search') || '');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const load = async (pageNum = 1, term = search) => {
    setLoading(true);
    setError('');
    const params = new URLSearchParams({ page: String(pageNum), per_page: '50' });
    if (term) {
      params.set('search', term);
    }
    try {
      const data = await apiJson(`/api/super-admin/clients?${params}`);
      setPage({
        data: data.data ?? [],
        total: data.total ?? 0,
        last_page: data.last_page ?? 1,
        current_page: data.current_page ?? 1,
      });
    } catch (e) {
      setError(e.message);
      setPage({ data: [], total: 0, last_page: 1, current_page: 1 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fromUrl = searchParams.get('search');
    if (fromUrl != null) {
      setSearch(fromUrl);
    }
    void load(1, fromUrl ?? search);
  }, [searchParams]);

  const rows = page.data;

  return (
    <div className="stack">
      <h2 data-tour="page-title">Client records</h2>
      <p className="muted small">
        Business clients created by agencies (e.g. Ivan Lychko). Portal login accounts are listed under{' '}
        <strong>Users</strong> after you create them on the client detail page.
      </p>
      {error ? <p className="err">{error}</p> : null}

      <form
        className="row-actions"
        style={{ marginBottom: '1rem' }}
        onSubmit={(e) => {
          e.preventDefault();
          void load(1);
        }}
      >
        <FormInput
          placeholder="Company, name, or email"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ maxWidth: 280 }}
        />
        <button type="submit" className="btn-primary">
          Search
        </button>
      </form>

      {loading ? <p className="muted">Loading…</p> : null}

      <div className="table-wrap" data-tour="clients-list">
        <table className="table table-striped table-hover data-table mb-0">
          <thead>
            <tr>
              <th>Company</th>
              <th>Name</th>
              <th>Email</th>
              <th>Organization</th>
              <th>Projects</th>
              <th>Portal logins</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && !loading ? (
              <tr>
                <td colSpan={6} className="muted">
                  No client records found.
                </td>
              </tr>
            ) : null}
            {rows.map((c) => (
              <tr key={c.id}>
                <td>{c.company_name || '—'}</td>
                <td>{c.name}</td>
                <td>{c.email || '—'}</td>
                <td>{c.organization?.name ?? '—'}</td>
                <td>{c.projects_count ?? 0}</td>
                <td>
                  {(c.portal_users ?? []).length === 0 ? (
                    <span className="muted">None</span>
                  ) : (
                    <ul className="list-plain small" style={{ margin: 0 }}>
                      {c.portal_users.map((u) => (
                        <li key={u.id}>
                          {u.name} · {u.email}
                          {!u.is_active ? ' (invite pending)' : ''}
                        </li>
                      ))}
                    </ul>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {page.last_page > 1 ? (
        <div className="row-actions">
          <button
            type="button"
            className="btn-secondary"
            disabled={page.current_page <= 1}
            onClick={() => void load(page.current_page - 1)}
          >
            Previous
          </button>
          <span className="muted small">
            Page {page.current_page} of {page.last_page} ({page.total} total)
          </span>
          <button
            type="button"
            className="btn-secondary"
            disabled={page.current_page >= page.last_page}
            onClick={() => void load(page.current_page + 1)}
          >
            Next
          </button>
        </div>
      ) : null}
    </div>
  );
}
